import {
  PromoPlanType,
  PromoTerm,
  PromoTermType,
  PromoTermUnit,
  PromoWeekday,
  ReachPromo,
  ReachPromoPayload,
} from "../../../apis/requests/promos/types";
import {
  ALLOWED_PLAN_TYPES_BY_TERM_TYPE,
  ALLOWED_UNITS_BY_TERM_TYPE,
  CADENCE_UNIT_DAYS,
  CadenceUnit,
  ALL_PLANS,
  DISCOUNT_TERM_TYPES,
  AppliesToOption,
  PLAN_TYPE_LABELS,
  PROMO_KEY_MAX_LENGTH,
  PROMO_PLAN_TYPES,
  PROMO_TERM_TYPES,
  PromoWindow,
  SETTABLE_STATUS_BY_STATUS,
  SINGLE_ONE_TIME_DISCOUNT,
  TERM_PLAN_TYPE_REPEATED,
  TERM_TYPE_SHORT_LABELS,
  UNITS_WITHOUT_AMOUNT,
  WINDOW_DURATION_HOURS,
} from "./constants";
import {effectiveStartDate, START_OF_DAY, toDateInput} from "./dates";
import {PromoFormValues, PromoTermFormValues} from "./schema";

export {todayDateInput} from "./dates";

export const toPromoKey = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .slice(0, PROMO_KEY_MAX_LENGTH)
    .replace(/^_+|_+$/g, "");

const toDateTime = (date: string): string | null => (date ? `${date}${START_OF_DAY}` : null);

const windowFromDuration = (durationHours: number | null): PromoWindow => {
  const presetWindow = (Object.keys(WINDOW_DURATION_HOURS) as Exclude<PromoWindow, "custom">[]).find(
    (window) => WINDOW_DURATION_HOURS[window] === durationHours
  );
  return presetWindow ?? "custom";
};

const durationFromWindow = (window: PromoWindow): number | null =>
  window === "custom" ? null : WINDOW_DURATION_HOURS[window];

const largestCadenceUnit = (cadenceDays: number): CadenceUnit => {
  if (cadenceDays % CADENCE_UNIT_DAYS.months === 0) return "months";
  if (cadenceDays % CADENCE_UNIT_DAYS.weeks === 0) return "weeks";
  return "days";
};

export const newPromoTerm = (
  type: PromoTermType = PROMO_TERM_TYPES[0],
  planTypes: PromoPlanType[] = ALLOWED_PLAN_TYPES_BY_TERM_TYPE[type]
): PromoTermFormValues => ({
  type,
  amount: null,
  unit: ALLOWED_UNITS_BY_TERM_TYPE[type][0],
  plan_types: [...planTypes],
});

const toPayloadTerm = (term: PromoTermFormValues): PromoTerm => ({
  type: term.type,
  unit: term.unit,
  amount: UNITS_WITHOUT_AMOUNT.has(term.unit) ? null : term.amount,
  plan_types: term.plan_types,
});

const isOneTimeDiscount = (term: PromoTermFormValues): boolean =>
  DISCOUNT_TERM_TYPES.has(term.type) && term.plan_types.includes("one_time_payment");

export const termConflict = (term: PromoTermFormValues, otherTerms: PromoTermFormValues[]): string | undefined => {
  const repeatsPlanType = otherTerms.some(
    (other) => other.type === term.type && other.plan_types.some((planType) => term.plan_types.includes(planType))
  );
  if (repeatsPlanType) return TERM_PLAN_TYPE_REPEATED;
  if (isOneTimeDiscount(term) && otherTerms.some(isOneTimeDiscount)) return SINGLE_ONE_TIME_DISCOUNT;
  return undefined;
};

const candidatePlanTypes = (type: PromoTermType): PromoPlanType[][] => {
  const allowed = ALLOWED_PLAN_TYPES_BY_TERM_TYPE[type];
  return allowed.length > 1 ? [allowed, ...allowed.map((planType) => [planType])] : [allowed];
};

export const nextAvailableTerm = (terms: PromoTermFormValues[]): PromoTermFormValues | undefined =>
  PROMO_TERM_TYPES.flatMap((type) => candidatePlanTypes(type).map((planTypes) => newPromoTerm(type, planTypes))).find(
    (candidate) => !termConflict(candidate, terms)
  );

const DEFAULT_START_WEEKDAY: PromoWeekday = "monday";
const DEFAULT_CADENCE_EVERY = 3;
const DEFAULT_CADENCE_UNIT: CadenceUnit = "weeks";

export const emptyPromoForm = (today: string): PromoFormValues => ({
  name: "",
  key: "",
  description: "",
  status: "draft",
  start_mode: "date",
  starts_at: today,
  start_weekday: DEFAULT_START_WEEKDAY,
  repeats: false,
  ends_at: "",
  window: "72h",
  cadence_every: DEFAULT_CADENCE_EVERY,
  cadence_unit: DEFAULT_CADENCE_UNIT,
  terms: [newPromoTerm()],
  segment_ids: [],
  templates: [],
  no_send_days: [],
  force_days: [],
  reminders: [],
});

export const promoToFormValues = (promo: ReachPromo): PromoFormValues => {
  const cadenceUnit = promo.cadence_days ? largestCadenceUnit(promo.cadence_days) : DEFAULT_CADENCE_UNIT;
  return {
    name: promo.name,
    key: promo.key,
    description: promo.description ?? "",
    status: SETTABLE_STATUS_BY_STATUS[promo.status],
    start_mode: promo.entry_weekday ? "weekday" : "date",
    starts_at: toDateInput(promo.starts_at),
    start_weekday: promo.entry_weekday ?? DEFAULT_START_WEEKDAY,
    repeats: promo.cadence_days !== null,
    ends_at: toDateInput(promo.ends_at),
    window: windowFromDuration(promo.duration_hours),
    cadence_every: promo.cadence_days ? promo.cadence_days / CADENCE_UNIT_DAYS[cadenceUnit] : DEFAULT_CADENCE_EVERY,
    cadence_unit: cadenceUnit,
    terms: promo.terms,
    segment_ids: promo.segments.map((segment) => segment.id),
    templates: promo.templates.map((template) => ({
      template_id: template.id,
      days_since_start: template.days_since_start,
    })),
    no_send_days: promo.no_send_days ?? [],
    force_days: [...new Set(promo.templates.flatMap((template) => template.days_since_start))].sort((a, b) => a - b),
    reminders: (promo.reminders ?? []).map(({day, delay_hours}) => ({day, delay_hours})),
  };
};

const cadenceDays = (values: PromoFormValues): number | null =>
  values.repeats && values.cadence_every !== null
    ? values.cadence_every * CADENCE_UNIT_DAYS[values.cadence_unit]
    : null;

export const formValuesToPayload = (values: PromoFormValues, today: string): ReachPromoPayload => ({
  name: values.name.trim(),
  key: values.key,
  description: values.description.trim() || null,
  status: values.status,
  starts_at: toDateTime(effectiveStartDate(values, today))!,
  ends_at: values.window === "custom" ? toDateTime(values.ends_at) : null,
  duration_hours: durationFromWindow(values.window),
  cadence_days: cadenceDays(values),
  terms: values.terms.map(toPayloadTerm),
  segment_ids: values.segment_ids,
  templates: values.templates,
  no_send_days: values.no_send_days,
  entry_weekday: values.start_mode === "weekday" ? values.start_weekday : null,
  reminders: values.reminders.map((reminder) => ({...reminder, delay_hours: reminder.delay_hours!})),
});

const TERM_VALUE_FORMATTERS: Record<PromoTermUnit, (term: PromoTerm) => string> = {
  percent: (term) => (term.type === "set_one_time_discount" ? `${term.amount}%` : `+${term.amount}%`),
  installments: (term) => `+${term.amount} installments`,
  sa_max: () => "max SA",
};

export const formatTermValue = (term: PromoTerm): string => TERM_VALUE_FORMATTERS[term.unit](term);

export const appliesToOption = (planTypes: PromoPlanType[]): AppliesToOption =>
  planTypes.length === 1 ? planTypes[0] : ALL_PLANS;

export const planTypesForOption = (option: AppliesToOption): PromoPlanType[] =>
  option === ALL_PLANS ? [...PROMO_PLAN_TYPES] : [option];

const planTypesSuffix = (term: PromoTerm): string =>
  term.plan_types.length === ALLOWED_PLAN_TYPES_BY_TERM_TYPE[term.type].length
    ? ""
    : ` (${term.plan_types.map((planType) => PLAN_TYPE_LABELS[planType]).join(", ")})`;

export const termChipLabel = (term: PromoTerm): string =>
  `${TERM_TYPE_SHORT_LABELS[term.type]} ${formatTermValue(term)}${planTypesSuffix(term)}`;

export const formatWindow = (promo: ReachPromo): string =>
  promo.duration_hours ? `${promo.duration_hours}h from user entry` : "Fixed dates";

export const creatorName = (principal: string | null): string => (principal ? principal.split("@")[0] : "");

export const parseNumberInput = (value: string): number | null => (value.trim() === "" ? null : Number(value));
const PROMO_ISSUE_MESSAGES = {
  noSegments: "No eligible segments. A linked segment may have been deleted.",
};

export const promoIssues = (promo: ReachPromo): string[] =>
  promo.segments.length === 0 ? [PROMO_ISSUE_MESSAGES.noSegments] : [];

const HOURS_PER_DAY = 24;
const MS_PER_DAY = HOURS_PER_DAY * 60 * 60 * 1000;

export const promoWindowDays = (values: Pick<PromoFormValues, "window" | "starts_at" | "ends_at">): number | null => {
  if (values.window !== "custom") return Math.ceil(WINDOW_DURATION_HOURS[values.window] / HOURS_PER_DAY);
  if (!values.starts_at || !values.ends_at) return null;
  const days = Math.round((Date.parse(values.ends_at) - Date.parse(values.starts_at)) / MS_PER_DAY) + 1;
  return days > 0 ? days : null;
};

export const MODEL_DAY_ACTION = "model";
export const NO_SEND_DAY_ACTION = "no_send";
export const FORCE_DAY_ACTION = "force";
export type DayAction = typeof MODEL_DAY_ACTION | typeof NO_SEND_DAY_ACTION | typeof FORCE_DAY_ACTION;

type DayPlan = Pick<PromoFormValues, "templates" | "no_send_days" | "force_days" | "reminders">;
export type ReminderFormValues = PromoFormValues["reminders"][number];

export const DEFAULT_REMINDER_DELAY_HOURS = 2;

const withoutDay = (days: number[], day: number): number[] => days.filter((value) => value !== day);

const withDay = (days: number[], day: number): number[] => [...withoutDay(days, day), day].sort((a, b) => a - b);

export const dayAction = (plan: DayPlan, day: number): DayAction => {
  if (plan.no_send_days.includes(day)) return NO_SEND_DAY_ACTION;
  if (plan.force_days.includes(day)) return FORCE_DAY_ACTION;
  return MODEL_DAY_ACTION;
};

export const withDayAction = (plan: DayPlan, day: number, action: DayAction): DayPlan => ({
  templates:
    action === FORCE_DAY_ACTION
      ? plan.templates
      : plan.templates.map((link) => ({...link, days_since_start: withoutDay(link.days_since_start, day)})),
  no_send_days: action === NO_SEND_DAY_ACTION ? withDay(plan.no_send_days, day) : withoutDay(plan.no_send_days, day),
  force_days: action === FORCE_DAY_ACTION ? withDay(plan.force_days, day) : withoutDay(plan.force_days, day),
  reminders: action === FORCE_DAY_ACTION ? plan.reminders : plan.reminders.filter((reminder) => reminder.day !== day),
});

export const forcedTemplateIds = (plan: DayPlan, day: number): number[] =>
  plan.templates.filter((link) => link.days_since_start.includes(day)).map((link) => link.template_id);

export const withForcedTemplates = (plan: DayPlan, day: number, templateIds: number[]): DayPlan["templates"] =>
  plan.templates.map((link) => ({
    ...link,
    days_since_start: templateIds.includes(link.template_id)
      ? withDay(link.days_since_start, day)
      : withoutDay(link.days_since_start, day),
  }));

export const reminderForDay = (plan: DayPlan, day: number): ReminderFormValues | undefined =>
  plan.reminders.find((reminder) => reminder.day === day);

export const withDayReminder = (
  plan: DayPlan,
  day: number,
  reminder: ReminderFormValues | null
): ReminderFormValues[] =>
  [...plan.reminders.filter((existing) => existing.day !== day), ...(reminder ? [reminder] : [])].sort(
    (a, b) => a.day - b.day
  );
